import React, {useState} from "react";
import {FaPen, FaPlus, FaTrash} from "react-icons/fa6";
import Button from "../../../../components/button";
import InputForm from "../../../../components/input-form";
import CardDetailPopover from "../card-detail-popover";
import {
    getColorPickerValue,
    getLabelColorClass,
    getLabelColorStyle,
    getLabelSolidColorClass,
    isHexColor,
    isValidLabelColor,
    labelColors,
    normalizeTypedColor,
} from "./label-utils";

const matchesPrimaryColor = (color, colorOption) => (
    getLabelColorClass(color) === getLabelColorClass(colorOption.value)
    || (isHexColor(color) && color.toLowerCase() === getColorPickerValue(colorOption.value).toLowerCase())
);

const prioritizePrimaryColors = labels => {
    const remainingLabels = [...labels];
    const usedLabelIds = new Set(labels.map(label => String(label.id)));
    const primaryLabels = labelColors.map((colorOption, index) => {
        const labelIndex = remainingLabels.findIndex(label => matchesPrimaryColor(label.color, colorOption));

        if (labelIndex >= 0) return remainingLabels.splice(labelIndex, 1)[0];

        const defaultId = `default-primary-label-${index}`;
        let uniqueId = defaultId;
        let suffix = 1;

        while (usedLabelIds.has(uniqueId)) {
            uniqueId = `${defaultId}-${suffix}`;
            suffix += 1;
        }

        usedLabelIds.add(uniqueId);

        return {
            id: uniqueId,
            text: "",
            color: colorOption.value,
        };
    });

    return [...primaryLabels, ...remainingLabels];
};

const CardLabelsPopover = ({labels, boardLabels, onUpdate, onCreateBoardLabel, onUpdateBoardLabel, onDeleteBoardLabel, onRemove, openOnMount, onOpenOnMount, readOnly = false}) => {
    const [labelName, setLabelName] = useState("");
    const [labelColor, setLabelColor] = useState(labelColors[0]);
    const [editingLabelId, setEditingLabelId] = useState(null);
    const [editorMode, setEditorMode] = useState("list");
    const [search, setSearch] = useState("");
    const [showAll, setShowAll] = useState(false);
    const normalizedSearch = search.trim().toLowerCase();
    const availableLabels = prioritizePrimaryColors(boardLabels);
    const filteredLabels = availableLabels.filter(label => String(label.text ?? "").toLowerCase().includes(normalizedSearch));
    const selectedLabelIds = new Set(labels.map(label => String(label.id)));
    const orderedLabels = [
        ...filteredLabels.filter(label => selectedLabelIds.has(String(label.id))),
        ...filteredLabels.filter(label => !selectedLabelIds.has(String(label.id))),
    ];
    const visibleLabels = showAll || normalizedSearch ? orderedLabels : orderedLabels.slice(0, 5);

    const resetEditor = () => {
        setEditingLabelId(null);
        setLabelName("");
        setLabelColor(labelColors[0]);
        setEditorMode("list");
    };

    const addLabel = () => {
        const text = labelName.trim();
        const label = {id: `label-${Date.now()}`, text, color: labelColor.value};
        onCreateBoardLabel?.(label);
        onUpdate({labels: [...labels, label]}, text ? `adicionou a etiqueta “${text}”` : "adicionou uma etiqueta sem nome");
        resetEditor();
    };

    const startCreating = () => {
        setEditingLabelId(null);
        setLabelName("");
        setLabelColor(labelColors[0]);
        setEditorMode("create");
    };

    const startEditing = label => {
        if (!boardLabels.some(boardLabel => String(boardLabel.id) === String(label.id))) {
            onCreateBoardLabel?.(label);
        }
        setEditingLabelId(label.id);
        setLabelName(label.text ?? "");
        setLabelColor(labelColors.find(option => option.value === label.color) ?? {value: label.color, label: "Cor atual"});
        setEditorMode("edit");
    };

    const saveLabel = changes => {
        if (editingLabelId === null) return;
        onUpdateBoardLabel?.(editingLabelId, changes ?? {text: labelName.trim(), color: labelColor.value});
    };

    const removeLabel = () => {
        if (editingLabelId === null) return;
        onDeleteBoardLabel?.(editingLabelId);
        resetEditor();
    };

    const toggleLabel = label => {
        const isSelected = labels.some(selectedLabel => String(selectedLabel.id) === String(label.id));
        if (!isSelected && !boardLabels.some(boardLabel => String(boardLabel.id) === String(label.id))) {
            onCreateBoardLabel?.(label);
        }
        onUpdate(
            {labels: isSelected ? labels.filter(selectedLabel => String(selectedLabel.id) !== String(label.id)) : [...labels, label]},
            `${isSelected ? "removeu" : "adicionou"} a etiqueta${label.text ? ` “${label.text}”` : " sem nome"}`,
        );
    };

    const handleOpenChange = open => {
        if (open) return;
        setSearch("");
        setShowAll(false);
        resetEditor();
    };

    return (
        <CardDetailPopover
            title="Etiquetas"
            panelTitle={editorMode === "list" ? "Etiquetas" : editorMode === "create" ? "Criar etiqueta" : "Editar etiqueta"}
            onRemove={onRemove}
            onOpenChange={handleOpenChange}
            openOnSummaryClick
            openOnMount={openOnMount}
            onOpenOnMount={onOpenOnMount}
            readOnly={readOnly}
            summary={labels.map((label, index) => (
                <button type="button" key={label.id ?? `${label.text}-${label.color}-${index}`} title={label.text ? `Etiqueta ${label.text}` : "Etiqueta sem nome"} style={getLabelColorStyle(label.color)} className={`flex h-8 w-20 items-center justify-center overflow-hidden rounded-full px-2 text-center text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300 ${getLabelSolidColorClass(label.color)}`}>
                    <span className="w-full truncate">{label.text || <span className="sr-only">Etiqueta sem nome</span>}</span>
                </button>
            ))}
        >
            {editorMode === "list" ? (
                <div>
                    <label htmlFor="card-label-search" className="sr-only">Buscar</label>
                    <input id="card-label-search" type="search" value={search} onChange={({target}) => setSearch(target.value)} placeholder="Buscar" className="mb-3 h-9 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                    <div className="space-y-1.5">
                        {visibleLabels.map((label, index) => {
                            const isSelected = labels.some(selectedLabel => String(selectedLabel.id) === String(label.id));
                            return (
                                <div key={label.id ?? `${label.text}-${label.color}-${index}`} className="flex items-center gap-2">
                                    <input type="checkbox" checked={isSelected} onChange={() => toggleLabel(label)} aria-label={`${isSelected ? "Remover" : "Adicionar"} etiqueta ${label.text || "sem nome"}`} className="h-4 w-4 shrink-0 cursor-pointer accent-blue-600" />
                                    <button type="button" onClick={() => toggleLabel(label)} title={label.text || "Etiqueta sem nome"} style={getLabelColorStyle(label.color)} className={`h-8 min-w-0 flex-1 truncate rounded px-3 text-left text-sm font-semibold transition-colors ${getLabelSolidColorClass(label.color)}`}>
                                        {label.text || <span className="sr-only">Etiqueta sem nome</span>}
                                    </button>
                                    <button type="button" onClick={() => startEditing(label)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Editar etiqueta" aria-label={`Editar etiqueta ${label.text || "sem nome"}`}><FaPen /></button>
                                </div>
                            );
                        })}
                        {filteredLabels.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Nenhuma etiqueta encontrada.</p>}
                    </div>
                    <Button type="button" onClick={startCreating} col="mt-4 w-full !border-slate-300 !text-slate-600">Criar uma nova etiqueta</Button>
                    {!normalizedSearch && filteredLabels.length > 5 && (
                        <Button type="button" onClick={() => setShowAll(current => !current)} col="mt-2 w-full !border-slate-300 !text-slate-600">
                            {showAll ? "Mostrar menos etiquetas" : "Mostrar mais etiquetas"}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid gap-3">
                    <InputForm id="label-name" label="Nome (opcional)" value={labelName} onChange={({target}) => setLabelName(target.value)} onBlur={() => editorMode === "edit" && saveLabel({text: labelName.trim()})} col="w-full" />
                    <div>
                        <p className="mb-2 text-xs font-semibold text-slate-600">Cores rápidas</p>
                        <div className="grid grid-cols-5 gap-2">
                            {labelColors.map(option => (
                                <button key={option.value} type="button" onClick={() => {
                                    setLabelColor(option);
                                    if (editorMode === "edit") saveLabel({color: option.value});
                                }} title={option.label} aria-label={`Selecionar cor ${option.label}`} aria-pressed={labelColor.value === option.value} className={`h-8 rounded transition focus:outline-none focus:ring-2 focus:ring-blue-300 ${getLabelSolidColorClass(option.value)} ${labelColor.value === option.value ? "ring-2 ring-offset-2 ring-slate-500" : ""}`} />
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="mb-1 text-xs font-semibold text-slate-600">Cor personalizada</p>
                        <div className={`flex h-10 items-center gap-2 rounded-md border bg-white p-1.5 focus-within:ring-2 ${isValidLabelColor(labelColor.value) ? "border-slate-300 focus-within:border-blue-500 focus-within:ring-blue-100" : "border-red-400 focus-within:ring-red-100"}`}>
                            <input type="color" value={getColorPickerValue(labelColor.value)} onChange={({target}) => setLabelColor({value: target.value, label: "Personalizada"})} onBlur={({target}) => editorMode === "edit" && saveLabel({color: target.value})} title="Seletor de cores" aria-label="Seletor de cores" className="h-7 w-10 shrink-0 cursor-pointer border-0 bg-transparent p-0" />
                            <input type="text" value={isHexColor(labelColor.value) || String(labelColor.value ?? "").startsWith("#") ? String(labelColor.value ?? "").toUpperCase() : getColorPickerValue(labelColor.value).toUpperCase()} onChange={({target}) => setLabelColor({value: normalizeTypedColor(target.value), label: "Personalizada"})} onBlur={() => editorMode === "edit" && isValidLabelColor(labelColor.value) && saveLabel({color: labelColor.value})} maxLength={7} placeholder="#2563EB" aria-label="Código hexadecimal da cor" className="min-w-0 flex-1 border-0 bg-transparent px-1 font-mono text-sm font-normal uppercase text-slate-600 outline-none" />
                        </div>
                        {!isValidLabelColor(labelColor.value) && <p className="mt-1 text-xs text-red-500">Informe uma cor hexadecimal com seis caracteres.</p>}
                    </div>
                    <div style={getLabelColorStyle(labelColor.value)} className={`h-9 truncate rounded px-3 py-2 text-sm font-semibold ${getLabelSolidColorClass(labelColor.value)}`}>{labelName || "Pré-visualização"}</div>
                    <div className="flex justify-end gap-2">
                        {editorMode === "edit" && <Button type="button" target="danger" onClick={removeLabel} ><FaTrash /> Excluir</Button>}
                        <Button type="button" onClick={resetEditor}>{editorMode === "create" ? "Cancelar" : "Voltar"}</Button>
                        {editorMode === "create" && <Button type="button" target="primary" onClick={addLabel} disabled={!isValidLabelColor(labelColor.value)}><FaPlus /> Criar</Button>}
                    </div>

                </div>
            )}
        </CardDetailPopover>
    );
};

export default CardLabelsPopover;
